import { CONNECTOR_LIMITS, CONNECTOR_MAP_TO_PATTERN } from "@sagot/constants";
import { bodyAllowed, bodyProblem, isForbiddenHeader, pathProblem, pointerProblem, rowProblems, rowsOf } from "./builder/connector-model";
import { outputProblems } from "./builder/connector-op-model";
import { kindOf } from "./definition-model";
import type { Problem } from "./problem-model";
import { isConnectorCall, type ConnectorCallStep, type Definition, type HttpRequestStep } from "./types";

/**
 * The rules of the backend's `graph_p5.py` for the Data connector step that need no database: credentials stay out of the
 * definition, a GET has no body, the body is JSON once its variables are plain values, each variable is filled once, a
 * request the visitor waits for is short. (The endpoint itself - it exists, it is enabled, it is "Only from workflows" -
 * is checked by the server against the database.) Each problem is `connector_*` at `step <id>`; the plan rule is the
 * generic `needs_plan` of `planProblems`.
 */

const problem = (code: string, step: HttpRequestStep | ConnectorCallStep, path: string, params: Problem["params"] = {}): Problem => ({
  code,
  where: `step ${step.id}`,
  path,
  stepId: step.id,
  params,
  source: "client",
});

function headerProblems(step: HttpRequestStep, path: string): Problem[] {
  const names = Object.keys(step.headers ?? {});
  const found: Problem[] = [];
  const forbidden = names.find(isForbiddenHeader);
  if (forbidden !== undefined) found.push(problem("connector_forbidden_header", step, path, { header: forbidden }));
  const lower = names.map((name) => name.toLowerCase());
  const repeated = lower.find((name, index) => lower.indexOf(name) !== index);
  if (repeated !== undefined) found.push(problem("connector_duplicate_header", step, path, { header: repeated }));
  return found;
}

function mapProblems(step: HttpRequestStep, path: string): Problem[] {
  const map = step.map ?? [];
  const found: Problem[] = [];
  if (map.some((row) => pointerProblem(row.from) !== null)) found.push(problem("connector_map_pointer", step, path));
  if (map.some((row) => !CONNECTOR_MAP_TO_PATTERN.test(row.to))) found.push(problem("connector_map_variable", step, path));
  const targets = map.map((row) => row.to);
  const repeated = targets.find((target, index) => targets.indexOf(target) !== index);
  if (repeated !== undefined) found.push(problem("connector_duplicate_target", step, path, { variable: repeated }));
  return found;
}

function requestProblems(step: HttpRequestStep, path: string, facing: boolean): Problem[] {
  const found: Problem[] = [...headerProblems(step, path), ...mapProblems(step, path)];
  const pathFault = pathProblem(step.path);
  if (pathFault === "dots") found.push(problem("connector_bad_path", step, path));
  else if (pathFault !== null) found.push(problem("connector_path_format", step, path));
  if (!bodyAllowed(step.method) && step.body) found.push(problem("connector_body_on_get", step, path));
  if (bodyAllowed(step.method) && bodyProblem(step.body) !== null) found.push(problem("connector_bad_body", step, path, { example: '"{{var.student_no}}"' }));
  const rows = [...rowProblems(rowsOf(step.query), "query"), ...rowProblems(rowsOf(step.headers), "header")];
  if (rows.some((row) => row === "name" || row === "value")) found.push(problem("connector_row", step, path));
  if (facing && step.timeout_ms > CONNECTOR_LIMITS.facingTimeoutMsMax) found.push(problem("connector_timeout_facing", step, path, { seconds: CONNECTOR_LIMITS.facingTimeoutMsMax / 1000 }));
  return found;
}

/** The whole-step rules of "Call connector operation" (backend `operation_problems`): an output copied twice, or two outputs filling one variable, or a target that is no run variable. */
function operationProblems(step: ConnectorCallStep, path: string): Problem[] {
  const names = (step.outputs ?? []).map((output) => output.name);
  const repeated = names.find((name, index) => names.indexOf(name) !== index);
  const found: Problem[] = [];
  if (repeated !== undefined) found.push(problem("connector_duplicate_output", step, path, { name: repeated }));
  const wrong = Object.entries(outputProblems(step));
  if (wrong.some(([, kind]) => kind === "variable")) found.push(problem("connector_map_variable", step, path));
  const duplicate = wrong.find(([, kind]) => kind === "duplicate");
  if (duplicate !== undefined) found.push(problem("connector_duplicate_target", step, path, { variable: (step.outputs ?? []).find((output) => output.name === duplicate[0])?.to ?? duplicate[0] }));
  return found;
}

export function connectorProblems(definition: Definition): Problem[] {
  const facing = kindOf(definition) === "facing";
  return Object.entries(definition.paths).flatMap(([path, body]) =>
    body.steps.flatMap((step) => (step.type === "http_request" ? (isConnectorCall(step) ? operationProblems(step, path) : requestProblems(step, path, facing)) : [])),
  );
}
